import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ userRole = 'dispatcher', notificationCounts = {} }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const navigationItems = [
    {
      label: 'Aufträge',
      path: '/order-management-dashboard',
      icon: 'ClipboardList',
      roles: ['dispatcher', 'admin', 'field_worker', 'technician'],
      badge:  0
    },
    {
      label: 'Scanner',
      path: '/vin-scanner',
      icon: 'Scan',
      roles: ['field_worker', 'technician', 'dispatcher', 'admin'],
      badge: 0
    },
    {
      label: 'Techniker',
      path: '/technician-assignment',
      icon: 'Users',
      roles: ['dispatcher', 'admin'],
      badge:  0
    },
    {
      label: 'Übersicht',
      path: '/revenue-analytics-dashboard',
      icon: 'BarChart3',
      roles: ['admin', 'dispatcher'],
      badge: 0
    },
    {
      label: 'Autohäuser',
      path: '/dealership-management',
      icon: 'Building2',
      roles: ['admin', 'dispatcher'],
      badge: 0
    }
  ];

  const moreMenuItems = [];

  const filteredNavItems = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const filteredMoreItems = moreMenuItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isActivePath = (path) => {
    if (path === '/order-management-dashboard' && location?.pathname === '/order-details') {
      return true;
    }
    if (path === '/technician-assignment' && location?.pathname === '/technician-mobile-view') {
      return true;
    }
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className="flex items-center space-x-3">
      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
        <Icon name="Car" size={20} color="white" />
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-semibold text-primary">Ragna Detailing</span>
        <span className="text-xs text-text-secondary -mt-1">Autohaus Verwaltung</span>
      </div>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-50">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo */}
        <div className="flex-shrink-0">
          <Logo />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {filteredNavItems?.map((item) => (
            <Button
              key={item?.path}
              variant={isActivePath(item?.path) ? "default" : "ghost"}
              onClick={() => handleNavigation(item?.path)}
              className="relative"
            >
              <Icon name={item?.icon} size={18} className="mr-2" />
              {item?.label}
              {item?.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {item?.badge > 99 ? '99+' : item?.badge}
                </span>
              )}
            </Button>
          ))}

          {/* More Menu */}
          {filteredMoreItems?.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="relative"
              >
                <Icon name="MoreHorizontal" size={18} className="mr-2" />
                Mehr
              </Button>
              
              {isMoreMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-lg shadow-modal z-1100">
                  {filteredMoreItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className="w-full flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <Icon name={item?.icon} size={16} className="mr-3" />
                      {item?.label}
                      {item?.badge > 0 && (
                        <span className="ml-auto bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                          {item?.badge > 99 ? '99+' : item?.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* User Menu */}
          <div className="relative ml-4">
            <Button
              variant="ghost"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2"
            >
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={16} className="text-primary" />
              </div>
              <span className="hidden md:block text-sm font-medium">
                {user?.email?.split('@')[0] || 'User'}
              </span>
              <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
            </Button>
            
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-lg shadow-modal z-1100">
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-medium text-popover-foreground">
                    {user?.email || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {userRole}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150"
                >
                  <Icon name="LogOut" size={16} className="mr-3" />
                  Abmelden
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden"
        >
          <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
        </Button>
      </div>
      {/* Overlay for mobile menu */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={() => {
            setIsMenuOpen(false);
            setIsMoreMenuOpen(false);
            setIsUserMenuOpen(false);
          }}
        />
      )}
      
      {/* Overlay for user menu */}
      {isUserMenuOpen && (
        <div 
          className="fixed inset-0 bg-transparent z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
      
      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border relative z-50">
          <nav className="px-4 py-2 space-y-1">
            {filteredNavItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} className="mr-3" />
                {item?.label}
                {item?.badge > 0 && (
                  <span className="ml-auto bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
              </button>
            ))}
            
            {filteredMoreItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} className="mr-3" />
                {item?.label}
                {item?.badge > 0 && (
                  <span className="ml-auto bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
              </button>
            ))}
            
            {/* Mobile User Menu */}
            <div className="border-t border-border pt-2 mt-2">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-foreground">
                  {user?.email || 'User'}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {userRole}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors duration-150"
              >
                <Icon name="LogOut" size={18} className="mr-3" />
                Abmelden
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;